<template>
    <div class="flex flex-col gap-6">

        <!-- Sticky banners -->
        <div v-if="showSuccess || showError"
             class="fixed left-0 right-0 top-0 z-30 flex justify-around px-4 pt-4 lg:left-64">
            <div class="w-full 2xl:w-3/4">
                <div v-if="showSuccess" class="flex items-start gap-3 rounded-md bg-green-50 px-4 py-3 shadow-md border-l-4 border-l-green-500/70">
                    <div class="flex-1">
                        <p class="m-0 text-sm font-semibold text-green-800">Settings saved</p>
                        <p class="m-0 text-sm text-green-700">Your changes have been applied.</p>
                    </div>
                    <button type="button" class="text-green-500/70 hover:text-green-700/70" @click="showSuccess = false">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>

                <div v-if="showError" class="flex items-start gap-3 rounded-md bg-red-50 px-4 py-3 shadow-md border-l-4 border-l-red-500/70">
                    <div class="flex-1">
                        <p class="m-0 text-sm font-semibold text-red-800">Could not save settings</p>
                        <p class="m-0 text-sm text-red-700">{{ errorMessage }}</p>
                    </div>
                    <button type="button" class="text-red-500/70 hover:text-red-700/70" @click="showError = false">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Category cards -->
        <div v-for="group in groups" :key="group.category" class="overflow-clip rounded-md border border-gray-200 bg-white">

            <!-- Header -->
            <div class="flex items-center gap-4 px-6 py-4">
                <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-500 text-white" v-html="iconFor(group.category)"></div>
                <div class="flex-1">
                    <p class="m-0 text-sm font-semibold text-gray-900">{{ group.label }}</p>
                    <p class="m-0 text-sm text-gray-500">{{ group.settings.length }} {{ group.settings.length === 1 ? 'setting' : 'settings' }}</p>
                </div>
            </div>

            <!-- Body -->
            <div class="border-t border-gray-200 bg-gray-50 px-6 py-5">
                <div class="divide-y divide-gray-200">
                    <div v-for="setting in group.settings" :key="setting.name"
                         class="grid grid-cols-1 gap-2 py-4 first:pt-0 last:pb-0 md:grid-cols-2 md:gap-4">

                        <div>
                            <p class="m-0 flex items-center gap-2 text-sm font-medium" :class="setting.can_be_changed ? 'text-gray-800' : 'text-gray-400'">
                                <span v-if="isDirty(setting.name)"
                                      class="h-2 w-2 shrink-0 rounded-full bg-amber-500"
                                      title="Unsaved change"></span>
                                <span :class="{ 'line-through': !setting.can_be_changed }">{{ setting.pretty_name }}</span>
                                <span v-if="!setting.can_be_changed" class="italic"> (not available for this feature level)</span>
                            </p>
                            <p v-if="setting.description" class="m-0 mt-1 text-sm text-gray-500">{{ setting.description }}</p>
                        </div>

                        <div>
                            <select2
                                v-if="setting.input_type === 'select'"
                                :options="setting.options"
                                v-model:value="values[setting.name]"
                                :name="setting.name"
                                :disabled="!setting.can_be_changed"
                            ></select2>
                            <input
                                v-else-if="setting.input_type === 'number'"
                                type="number"
                                min="1"
                                step="1"
                                v-model="values[setting.name]"
                                :disabled="!setting.can_be_changed"
                                class="w-full rounded-md border border-gray-300 bg-white p-2 text-sm shadow-sm"
                            />
                            <input
                                v-else
                                type="text"
                                v-model="values[setting.name]"
                                :disabled="!setting.can_be_changed"
                                class="w-full rounded-md border border-gray-300 bg-white p-2 text-sm shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sticky save footer -->
        <div v-if="canSave" class="fixed bottom-0 left-0 right-0 z-20 flex justify-around border-t border-gray-200 bg-white/95 backdrop-blur lg:left-64">
            <div class="flex w-full items-center justify-between gap-4 px-6 py-3 2xl:w-3/4">
                <div class="flex items-center gap-2 text-sm">
                    <template v-if="dirtyCount > 0">
                        <span class="h-2 w-2 shrink-0 rounded-full bg-amber-500"></span>
                        <span class="font-medium text-gray-700">
                            {{ dirtyCount }} unsaved {{ dirtyCount === 1 ? 'change' : 'changes' }}
                        </span>
                    </template>
                    <span v-else class="text-gray-400">All changes saved</span>
                </div>
                <div class="w-full lg:w-48">
                    <button type="button"
                            class="relative inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-blue-500 focus:border-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            :disabled="saving || dirtyCount === 0"
                            @click="save">
                        {{ saving ? 'Saving...' : 'Save changes' }}
                    </button>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'SystemSettingsPage',
    props: {
        groups: { type: Array, default: () => [] },
        companyId: { type: String, required: true },
        canSave: { type: Boolean, default: false },
    },
    data() {
        const values = {};
        this.groups.forEach(group => {
            group.settings.forEach(setting => {
                values[setting.name] = setting.value !== null && setting.value !== undefined
                    ? setting.value
                    : (setting.input_type === 'select' ? setting.select_default : '');
            });
        });
        return {
            values,
            // Captured after mount, once select2 has emitted its settled values, so the
            // baseline reflects what is actually rendered (some seeded values, like the -1
            // "not set" sentinel, are not valid options and get normalized by select2).
            originalValues: {},
            baselineCaptured: false,
            saving: false,
            showSuccess: false,
            showError: false,
            errorMessage: '',
            successTimeout: null,
        };
    },
    mounted() {
        // select2 emits its settled value asynchronously on mount (it debounces value
        // updates by ~100ms), so wait past that before snapshotting the baseline. Otherwise
        // values normalized by select2 (e.g. a seeded -1 "not set" that is not a valid option)
        // would read as dirty on an untouched page.
        setTimeout(() => {
            this.captureBaseline();
        }, 150);
    },
    computed: {
        dirtyKeys() {
            if (! this.baselineCaptured) {
                return [];
            }

            return Object.keys(this.values).filter(
                name => String(this.values[name]) !== String(this.originalValues[name])
            );
        },
        dirtyCount() {
            return this.dirtyKeys.length;
        },
    },
    methods: {
        captureBaseline() {
            this.originalValues = { ...this.values };
            this.baselineCaptured = true;
        },
        isDirty(name) {
            return this.baselineCaptured
                && String(this.values[name]) !== String(this.originalValues[name]);
        },
        iconFor(category) {
            const icons = {
                general: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>',
                user_roles: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
                alarms: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>',
                reporting: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
                devices: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>',
                compliance: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>',
            };
            return icons[category] || icons.general;
        },
        save() {
            this.saving = true;
            this.showSuccess = false;
            this.showError = false;

            axios.post('/api-internal/' + this.companyId + '/system-settings', this.values)
                .then(() => {
                    this.captureBaseline();
                    this.showSuccess = true;
                    if (this.successTimeout) {
                        clearTimeout(this.successTimeout);
                    }
                    this.successTimeout = setTimeout(() => {
                        this.showSuccess = false;
                    }, 5000);
                })
                .catch(() => {
                    this.errorMessage = 'Please try again. If the problem continues, contact support.';
                    this.showError = true;
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>
